import { Link } from 'react-router-dom';
import {
  LegalPage,
  Section, P, Table, Th, Td, Note, Ul, Ol, B,
  useLegalDocumentHead,
} from './components';

/**
 * 계정 및 데이터 삭제 요청 안내.
 *
 * Google Play 정책상 계정 생성이 가능한 앱은 앱 내 삭제 경로와 별도로
 * "앱 설치 없이 접근 가능한 웹 삭제 요청 URL"을 제공해야 하며,
 * 이 URL을 Play Console 데이터 안전 섹션에 등록한다.
 * (Apple은 앱 내 삭제 경로만 요구하므로 iOS 심사에는 필수가 아니다.)
 *
 * 삭제·보관 항목은 개인정보처리방침 6·7과 반드시 일치해야 한다.
 * 처리방침을 수정할 때 이 페이지도 함께 갱신할 것.
 */
export default function AccountDeletion() {
  useLegalDocumentHead('계정 및 데이터 삭제 | Cupid');

  return (
    <LegalPage
      title="계정 및 데이터 삭제"
      subtitle="애플리케이션: Cupid (그룹 매칭 데이팅 서비스)"
      intro={
        <P>
          이 페이지는 <B>Cupid</B> 이용자가 계정 삭제(회원 탈퇴)와 개인정보 삭제를 요청하는 방법을 안내합니다. 앱을 이미
          삭제하셨거나 로그인이 어려운 경우에도 아래 방법으로 요청하실 수 있습니다.
        </P>
      }
    >

        <Section no={1} title="앱에서 직접 삭제하기">
          <P>가장 빠른 방법입니다. 요청 즉시 처리됩니다.</P>
          <Ol>
            <li>Cupid 앱을 실행하고 로그인합니다.</li>
            <li>홈 화면 오른쪽 위의 <B>톱니바퀴(환경설정)</B>를 누릅니다.</li>
            <li>환경설정 화면 맨 아래의 <B>회원 탈퇴</B>를 선택합니다.</li>
            <li>안내를 확인한 뒤 <B>탈퇴하기</B>를 눌러 완료합니다.</li>
          </Ol>
          <Note>
            탈퇴 시 참여 중인 그룹에서 자동으로 나가게 되며, 친구 관계가 삭제됩니다. 보유 중인 하트는 <B>환불되지 않으며 소멸</B>
            되므로, 잔여 하트가 있다면 사용 후 탈퇴하시기 바랍니다.
          </Note>
        </Section>

        <Section no={2} title="앱 접근이 어려운 경우 — 이메일로 요청하기">
          <P>
            앱을 삭제하셨거나 로그인이 되지 않는 경우, 아래 주소로 삭제를 요청해 주세요.
          </P>
          <Table>
            <thead><tr><Th colSpan={2}>삭제 요청 접수처</Th></tr></thead>
            <tbody>
              <tr>
                <Td>전자우편</Td>
                <Td><a className="text-red-400 hover:text-red-300" href="mailto:support@cupidlab.io?subject=계정%20삭제%20요청">support@cupidlab.io</a></Td>
              </tr>
              <tr><Td>제목</Td><Td>계정 삭제 요청</Td></tr>
              <tr><Td>본문에 기재할 내용</Td><Td>가입 시 사용한 휴대전화번호, 이름</Td></tr>
            </tbody>
          </Table>
          <P>
            본인 확인 절차를 거친 뒤 처리하며, 접수일로부터 <B>영업일 기준 10일 이내</B>에 처리 결과를 회신합니다. 본인 확인이
            어려운 경우 요청이 거절될 수 있습니다.
          </P>
        </Section>

        <Section no={3} title="삭제되는 데이터">
          <P>계정 삭제 시 다음 정보가 삭제되거나 복원할 수 없도록 대체됩니다.</P>
          <Ul>
            <li>이름, 휴대전화번호, 기기 식별자, 푸시 알림 토큰</li>
            <li>생년월일, 성별, 직업</li>
            <li>프로필 사진 (저장된 이미지 파일 포함)</li>
            <li>친구 관계, 그룹 참여 정보</li>
            <li>위치정보</li>
            <li>보유 중인 하트 잔액</li>
          </Ul>
        </Section>

        <Section no={4} title="삭제 후에도 일정 기간 보관되는 데이터">
          <P>
            다음 정보는 다른 이용자의 보호, 분쟁 대응 또는 관계 법령에 따라 아래 기간 동안 보관한 뒤 파기됩니다. 자세한 내용은{' '}
            <Link to="/privacy" className="text-red-400 hover:text-red-300">개인정보처리방침</Link>의 6·7항을 참고해 주세요.
          </P>
          <Table>
            <thead><tr><Th>항목</Th><Th>보관 기간</Th><Th>사유</Th></tr></thead>
            <tbody>
              <tr>
                <Td>채팅 메시지</Td>
                <Td>대화 상대방이 보관하는 동안</Td>
                <Td>대화 상대방의 기록 보호 (발신자 표시는 "탈퇴한 사용자"로 대체)</Td>
              </tr>
              <tr>
                <Td>중복가입확인정보(DI)의 일방향 암호화 값</Td>
                <Td>3년</Td>
                <Td>신고·제재 회피를 위한 재가입 방지</Td>
              </tr>
              <tr><Td>신고 접수·처리 내역</Td><Td>3년</Td><Td>분쟁 대응</Td></tr>
              <tr><Td>결제 및 재화 공급에 관한 기록</Td><Td>5년</Td><Td>전자상거래 등에서의 소비자보호에 관한 법률</Td></tr>
              <tr><Td>서비스 접속기록</Td><Td>3개월</Td><Td>통신비밀보호법</Td></tr>
            </tbody>
          </Table>
        </Section>

        <Section no={5} title="유의사항">
          <Ul>
            <li>계정 삭제는 <B>취소할 수 없으며</B>, 삭제된 정보는 복구되지 않습니다.</li>
            <li>보유 중인 하트는 삭제와 함께 소멸되며 환불되지 않습니다.</li>
            <li>App Store 또는 Google Play를 통해 결제한 내역의 환불은 결제하신 스토어의 환불 정책을 따릅니다.</li>
            <li>탈퇴 후 재가입은 가능하나, 이전 계정의 친구·그룹·채팅 기록은 복원되지 않으며 신규 가입 혜택이 제공되지 않을 수 있습니다.</li>
          </Ul>
        </Section>

        <Section no={6} title="문의">
          <P>
            계정 삭제와 관련한 문의는{' '}
            <a className="text-red-400 hover:text-red-300" href="mailto:support@cupidlab.io">support@cupidlab.io</a>
            로 연락해 주시기 바랍니다.
          </P>
        </Section>

    </LegalPage>
  );
}
